import {
  AssetWatchLogo,
  Button,
  Checkbox,
  Headline,
  Input,
  Stack,
  Text,
  Title,
} from "@assetwatch/ui";
import type { ChangeEvent } from "react";
import type { AuthType, CustomLoginFormMessages } from "./CustomLoginForm";
import type { PasswordValidation } from "./loginUtils";
import { PasswordRequirements } from "./PasswordRequirements";

type LoginHeaderProps = {
  authType: AuthType;
  firstSignIn: boolean;
  isTemporaryPasswordChallenge: boolean;
  messages: CustomLoginFormMessages;
};

export function LoginHeader({
  authType,
  firstSignIn,
  isTemporaryPasswordChallenge,
  messages,
}: LoginHeaderProps) {
  return (
    <Stack align="center" gap={16}>
      {isTemporaryPasswordChallenge ? (
        <AssetWatchLogo size="display" aria-hidden />
      ) : (
        <AssetWatchLogo aria-hidden size="stacked" tone="default" />
      )}
      {isTemporaryPasswordChallenge ? (
        <>
          <Headline align="center" as="h1" size="sm">
            {messages.headings.updatePasswordTitle}
          </Headline>
          <Text align="center" tone="subtext">
            {messages.headings.updatePasswordDescription}
          </Text>
        </>
      ) : (authType === "basic" || authType === "temporaryPassword") &&
        !firstSignIn ? (
        <Text align="center" tone="subtext">
          {messages.headings.signInDescription}
        </Text>
      ) : (
        <Headline align="center" as="h1" size="sm">
          {messages.headings.signInTitle}
        </Headline>
      )}
    </Stack>
  );
}

type EmailLookupFieldsProps = {
  authType: AuthType;
  email: string;
  emailError: string | null;
  isCheckingEmail: boolean;
  isRememberMeEnabled: boolean;
  messages: CustomLoginFormMessages;
  onCheckEmail: () => void;
  onEmailChange: (email: string) => void;
  onRememberMeChange: (checked: boolean) => void;
};

export function EmailLookupFields({
  authType,
  email,
  emailError,
  isCheckingEmail,
  isRememberMeEnabled,
  messages,
  onCheckEmail,
  onEmailChange,
  onRememberMeChange,
}: EmailLookupFieldsProps) {
  if (
    authType === "mfacode" ||
    authType === "basic" ||
    authType === "temporaryPassword"
  ) {
    return null;
  }

  return (
    <>
      <Input
        autoFocus
        data-testid="email-input"
        disabled={Boolean(authType) || isCheckingEmail}
        error={emailError ?? undefined}
        label={messages.labels.email}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          onEmailChange(event.currentTarget.value)
        }
        placeholder={messages.placeholders.email}
        reserveSubtextSpace={false}
        type="email"
        value={email}
      />
      {authType === "" ? (
        <Stack gap={40}>
          <Checkbox
            checked={isRememberMeEnabled}
            data-testid="remember-me"
            label={messages.actions.rememberMe}
            onCheckedChange={(checked: boolean) =>
              onRememberMeChange(Boolean(checked))
            }
            size="sm"
          />
          <Button
            data-testid="continue-button"
            fullWidth
            loading={isCheckingEmail}
            loadingText={messages.loading.checking}
            disabled={Boolean(emailError)}
            type="button"
            onClick={onCheckEmail}
          >
            {messages.actions.continue}
          </Button>
        </Stack>
      ) : null}
    </>
  );
}

type TemporaryPasswordFieldsProps = {
  canSubmit: boolean;
  email: string;
  isLoading: boolean;
  isPasswordVisible: boolean;
  messages: CustomLoginFormMessages;
  onPasswordChange: (password: string) => void;
  onPasswordVisibilityChange: () => void;
  password: string;
  passwordError: string | null;
  passwordVisibilityLabel: string;
};

export function TemporaryPasswordFields({
  canSubmit,
  email,
  isLoading,
  isPasswordVisible,
  messages,
  onPasswordChange,
  onPasswordVisibilityChange,
  password,
  passwordError,
  passwordVisibilityLabel,
}: TemporaryPasswordFieldsProps) {
  return (
    <Stack gap={40}>
      <Stack gap={16}>
        <Text>
          {messages.copy.temporaryPasswordPrompt}
          <br />
          <strong>{email.trim().toLowerCase()}</strong>
        </Text>
        <PasswordInput
          autoFocus
          error={passwordError}
          isVisible={isPasswordVisible}
          label={messages.labels.password}
          onChange={onPasswordChange}
          onVisibilityChange={onPasswordVisibilityChange}
          reserveSubtextSpace={false}
          value={password}
          visibilityLabel={passwordVisibilityLabel}
        />
      </Stack>
      <Button
        data-testid="login-button"
        disabled={!canSubmit}
        fullWidth
        loading={isLoading}
        loadingText={messages.loading.loggingIn}
        type="submit"
      >
        {messages.actions.login}
      </Button>
      <Text align="center">{messages.copy.temporaryPasswordHelp}</Text>
    </Stack>
  );
}

type BasicPasswordFieldsProps = {
  canSubmit: boolean;
  email: string;
  forgotPasswordUrl?: string;
  isLoading: boolean;
  isPasswordVisible: boolean;
  messages: CustomLoginFormMessages;
  onChangeEmail: () => void;
  onPasswordChange: (password: string) => void;
  onPasswordVisibilityChange: () => void;
  password: string;
  passwordError: string | null;
  passwordVisibilityLabel: string;
};

export function BasicPasswordFields({
  canSubmit,
  email,
  forgotPasswordUrl,
  isLoading,
  isPasswordVisible,
  messages,
  onChangeEmail,
  onPasswordChange,
  onPasswordVisibilityChange,
  password,
  passwordError,
  passwordVisibilityLabel,
}: BasicPasswordFieldsProps) {
  return (
    <Stack align="center" gap={40}>
      <Button
        data-testid="change-email-button"
        icon="edit-pen"
        size="sm"
        type="button"
        variant="outline"
        onClick={onChangeEmail}
      >
        {email.trim().toLowerCase()}
      </Button>
      <Stack gap={4} fullWidth>
        <PasswordInput
          autoFocus
          error={passwordError}
          isVisible={isPasswordVisible}
          label={messages.labels.password}
          onChange={onPasswordChange}
          onVisibilityChange={onPasswordVisibilityChange}
          reserveSubtextSpace={false}
          value={password}
          visibilityLabel={passwordVisibilityLabel}
        />
        {forgotPasswordUrl ? (
          <Stack align="end" fullWidth>
            <Button
              render={
                <a href={forgotPasswordUrl}>
                  {messages.actions.forgotPassword}
                </a>
              }
              size="sm"
              variant="ghost"
            >
              {messages.actions.forgotPassword}
            </Button>
          </Stack>
        ) : null}
      </Stack>
      <Button
        data-testid="login-button"
        disabled={!canSubmit}
        fullWidth
        loading={isLoading}
        loadingText={messages.loading.loggingIn}
        type="submit"
      >
        {messages.actions.login}
      </Button>
    </Stack>
  );
}

type NewPasswordFieldsProps = {
  canSubmit: boolean;
  confirmPassword: string;
  confirmPasswordVisibilityLabel: string;
  isConfirmPasswordVisible: boolean;
  isLoading: boolean;
  isNewPasswordVisible: boolean;
  isTemporaryPasswordChallenge: boolean;
  messages: CustomLoginFormMessages;
  newPassword: string;
  newPasswordError: string | null;
  newPasswordVisibilityLabel: string;
  onConfirmPasswordChange: (password: string) => void;
  onConfirmPasswordVisibilityChange: () => void;
  onNewPasswordChange: (password: string) => void;
  onNewPasswordVisibilityChange: () => void;
  passedPasswordHistory?: boolean;
  passwordValidation: PasswordValidation;
};

export function NewPasswordFields({
  canSubmit,
  confirmPassword,
  confirmPasswordVisibilityLabel,
  isConfirmPasswordVisible,
  isLoading,
  isNewPasswordVisible,
  isTemporaryPasswordChallenge,
  messages,
  newPassword,
  newPasswordError,
  newPasswordVisibilityLabel,
  onConfirmPasswordChange,
  onConfirmPasswordVisibilityChange,
  onNewPasswordChange,
  onNewPasswordVisibilityChange,
  passedPasswordHistory,
  passwordValidation,
}: NewPasswordFieldsProps) {
  const submitLabel = isTemporaryPasswordChallenge
    ? (messages.actions.saveChanges ?? "Save changes")
    : messages.actions.setNewPassword;

  return (
    <>
      <Stack gap={16}>
        {isTemporaryPasswordChallenge ? null : (
          <Text tone="subtext">{messages.copy.newPasswordPrompt}</Text>
        )}
        <PasswordInput
          dataTestId="new-password-input"
          error={newPasswordError}
          isVisible={isNewPasswordVisible}
          label={messages.labels.newPassword}
          onChange={onNewPasswordChange}
          onVisibilityChange={onNewPasswordVisibilityChange}
          value={newPassword}
          visibilityLabel={newPasswordVisibilityLabel}
        />
        <PasswordRequirements
          isPassedPasswordHistory={passedPasswordHistory}
          labels={messages.passwordRequirements}
          requirements={passwordValidation}
          showPreviousPasswordRequirement={!isTemporaryPasswordChallenge}
          title={
            isTemporaryPasswordChallenge
              ? messages.copy.passwordRequirementsHeading
              : undefined
          }
          variant={isTemporaryPasswordChallenge ? "panel" : "list"}
        />
        <PasswordInput
          dataTestId="confirm-password-input"
          error={
            confirmPassword.length > 0 && !passwordValidation.matchesConfirm
              ? messages.copy.confirmPasswordMismatch
              : null
          }
          isVisible={isConfirmPasswordVisible}
          label={messages.labels.confirmPassword}
          onChange={onConfirmPasswordChange}
          onVisibilityChange={onConfirmPasswordVisibilityChange}
          value={confirmPassword}
          visibilityLabel={confirmPasswordVisibilityLabel}
        />
      </Stack>
      <Button
        data-testid="login-button"
        disabled={!canSubmit}
        fullWidth
        loading={isLoading}
        loadingText={messages.loading.updatingPassword}
        type="submit"
      >
        {submitLabel}
      </Button>
    </>
  );
}

type MfaFieldsProps = {
  canSubmit: boolean;
  isLoading: boolean;
  messages: CustomLoginFormMessages;
  mfaCode: string;
  onMfaCodeChange: (code: string) => void;
};

export function MfaFields({
  canSubmit,
  isLoading,
  messages,
  mfaCode,
  onMfaCodeChange,
}: MfaFieldsProps) {
  return (
    <>
      <Stack gap={16}>
        <Stack gap={8}>
          <Title as="h2" size="lg">
            {messages.headings.twoFactorTitle}
          </Title>
          <Text tone="subtext">{messages.headings.twoFactorDescription}</Text>
        </Stack>
        <Input
          autoFocus
          data-testid="mfa-code-input"
          label={messages.labels.verificationCode}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onMfaCodeChange(event.currentTarget.value)
          }
          type="text"
          value={mfaCode}
        />
      </Stack>
      <Button
        data-testid="submit-mfa"
        disabled={!canSubmit}
        fullWidth
        loading={isLoading}
        loadingText={messages.loading.verifying}
        type="submit"
      >
        {messages.actions.submit}
      </Button>
    </>
  );
}

type DisabledPasswordFieldProps = {
  messages: CustomLoginFormMessages;
  password: string;
};

export function DisabledPasswordField({
  messages,
  password,
}: DisabledPasswordFieldProps) {
  return (
    <Input
      data-testid="password-input"
      disabled
      label={messages.labels.password}
      type="password"
      value={password}
    />
  );
}

type PasswordInputProps = {
  autoFocus?: boolean;
  dataTestId?: string;
  error: string | null;
  isVisible: boolean;
  label: string;
  onChange: (password: string) => void;
  onVisibilityChange: () => void;
  reserveSubtextSpace?: boolean;
  value: string;
  visibilityLabel: string;
};

function PasswordInput({
  autoFocus,
  dataTestId = "password-input",
  error,
  isVisible,
  label,
  onChange,
  onVisibilityChange,
  reserveSubtextSpace = true,
  value,
  visibilityLabel,
}: PasswordInputProps) {
  return (
    <Input
      autoFocus={autoFocus}
      data-testid={dataTestId}
      error={error ?? undefined}
      label={label}
      onChange={(event: ChangeEvent<HTMLInputElement>) =>
        onChange(event.currentTarget.value)
      }
      reserveSubtextSpace={reserveSubtextSpace}
      trailingAction={{
        icon: isVisible ? "eye-watching" : "eye-slash-regular",
        "aria-label": visibilityLabel,
        "aria-pressed": isVisible,
        onClick: onVisibilityChange,
      }}
      type={isVisible ? "text" : "password"}
      value={value}
    />
  );
}
